import { List } from "immutable"
import 'intersection-observer'
import * as moment from 'moment'
import * as React from 'react'
import { AsyncState, IOWidget, Link as LinkComponent, Updater, async, fromJSX } from 'widgets-for-react'
import { Link } from '../../../../cms/blocks/types.gen'
import { link } from '../../../../cms/blocks/utils.gen'
import { PaginatedItem } from '../../../../drupal/types_and_parser/extra_types.mod'
import { ParsedNodeStory } from '../../../../drupal/types_and_parser/types'
import { CMSState } from "../../../drupalPage/state"
import { getLangFromUrl } from '../../../utils/history.mod'

export type PressReleaseItem = {
  title: string // "Refresco delivers robust Q2 2020 results",
  description: string // "<p><strong>Refresco delivers robust Q2 2020 results</strong></p>",
  published_at: string // "Thu, 13 Aug 2020 06:30 GMT",
  subject: string
  xml_link: string // "https://www.globenewswire.com/HexMLItem/Content/FullText/Attachments/All/Identifier/2077636/language/en",
  id: string // "2077636",
  category: string // "7125",
  text: null
}

export type pressReleasesBlockProps = {
  blockIndex: number
  item_limit?: number
  show_year_filter?: boolean
  button?: Link
  press_releases: AsyncState<PaginatedItem<PressReleaseItem>>
}

export type pressReleasesBlockStyle = Partial<{
  block_classname: string
  inview_classname: string
  inner_classname: string
  content_classname: string
  footer_classname: string
  clickable_classname: string
  visual_classname: string
}>

export function pressReleasesBlock(
  props: pressReleasesBlockProps,
  options?: { style: pressReleasesBlockStyle; key?: string }
): IOWidget<CMSState, Updater<CMSState>> {
  return (s: CMSState) =>
    fromJSX(
      (setState) => {
        return (
          <section className="block block--releases">
            <div className="block__inner container container--m grid--block">
              <header className="block__header">
                <h2 className="block__title title--l">Press releases</h2>
              </header>

              <div className="block__content">
                {async(props.press_releases).run((a) => {
                  setState((s0) => {
                    // let block = s0.blocks.get(props.block_index) as storiesBlockData
                    let block = s0.blocks.get(props.blockIndex) as any
                    block.value[0].press_releases = a(block.value[0].press_releases) as AsyncState<
                      PaginatedItem<ParsedNodeStory>
                    >
                    return { ...s0, blocks: s0.blocks.set(props.blockIndex, block) }
                  })
                })}
                {props.press_releases.kind == 'loaded' ? (
                  <PressReleasesGrid
                    items={props.press_releases.value.items}
                    item_limit={props.item_limit}
                    show_year_filter={props.show_year_filter}
                  />
                ) : (
                  <div className="loader">
                    <div className="loader__inner"></div>
                  </div>
                )}
              </div>
              {props.button && (
                <footer className="block__footer container container--m grid--gutter">
                  {link(props.button, { classname: 'button button--primary' }, props.button.title)}
                </footer>
              )}
            </div>
          </section>
        )
      }
    )
}

type PressReleasesGridProps = {
  items: Immutable.List<PressReleaseItem>
  item_limit?: number
  show_year_filter?: boolean
}
type PressReleasesGridState = {
  year: number
}

class PressReleasesGrid extends React.Component<PressReleasesGridProps, PressReleasesGridState> {
  // Only used when the feed carries no usable dates, the picker needs at least
  // one tab to render.
  static fallback_years = [2026, 2025, 2024, 2023, 2022]

  years: number[]

  constructor(props: PressReleasesGridProps) {
    super(props)

    this.years = PressReleasesGrid.yearsOf(props.items)
    this.state = {
      year: this.years[0]
    }
  }

  // The years come from the feed itself - a hardcoded list silently drops the
  // press releases of every year that is not in it.
  static yearsOf(items: PressReleasesGridProps['items']): number[] {
    let list: PressReleaseItem[] = Array.isArray(items) ? items : items.toArray()
    let years = list
      .map((press_release) => moment(press_release.published_at).year())
      .filter((year, index, all) => !isNaN(year) && all.indexOf(year) == index)
      .sort((a, b) => b - a)

    return years.length > 0 ? years : PressReleasesGrid.fallback_years
  }

  render() {
    let items = Array.isArray(this.props.items) ? List(this.props.items) : this.props.items

    // The year is filtered out before the limit is applied, otherwise the limit
    // eats items of the selected year that sit further down the feed.
    let press_releases = (this.props.show_year_filter
      ? items.filter((press_release) => moment(press_release.published_at).year() == this.state.year)
      : items)
      .sort((a, b) => moment(b.published_at).unix() - moment(a.published_at).unix())
      .slice(0, this.props.item_limit ? this.props.item_limit : items.size)
    return (
      <>
        {this.props.show_year_filter &&
          <ul className="tabs">
            {this.years.map((year) => (
              <li key={year} className={`tabs__item tab${this.state.year == year ? ' tab--active' : ''}`}>
                <button className={`tab__button`} onClick={(e) => this.setState({ year: year })}>
                  {year}
                </button>
              </li>
            ))}
          </ul>
        }
        <ul className="block__list grid">
          {press_releases.map((press_release) => {
            return (
              <li key={press_release.id} className="grid__item">
                <article className="grid__article">
                  <LinkComponent
                    className="grid__link"
                    to={`/${getLangFromUrl()}/press-releases/${press_release.id}`}
                  >
                    <div className="grid__content">
                      <header className="grid__header">
                        <span className="grid__label block__label label--m">
                          {moment(press_release.published_at).format('D MMMM YYYY')}
                        </span>
                        <h3 className="grid__title title--xs">{press_release.title}</h3>
                      </header>
                      <span className="grid__fauxlink link">Read more</span>
                    </div>
                  </LinkComponent>
                </article>
              </li>
            )
          }).toArray()}
        </ul>
      </>
    )
  }
}
